import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api, { corpusFundAPI } from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';
import '../../styles/ThemeVariables.css';
import '../../styles/Dashboard.css';
import axios from 'axios';

export default function CorpusFund() {
    const { formatCurrency, settings } = useSettings();
    
    const formatDate = (dateString) => {
        if (!dateString || typeof dateString !== 'string') return 'N/A';

        try {
            // Parse "YYYY-MM-DD HH:mm:ss" as UTC
            const [datePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-').map(Number);

            // Create a UTC Date object
            const utcDate = new Date(Date.UTC(year, month - 1, day));

            // Format to DD/MM/YYYY in IST timezone (same as financials page)
            return new Intl.DateTimeFormat('en-GB', {
                timeZone: 'Asia/Kolkata',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            }).format(utcDate);
        } catch (err) {
            console.error('Invalid date string:', dateString, err);
            return 'Invalid Date';
        }
    };
    
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState({
        total_income: 0,
        total_expenses: 0,
        balance: 0,
        total_transactions: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState(null);
    const [viewingTransaction, setViewingTransaction] = useState(null);
    const [formData, setFormData] = useState({
        type: 'income',
        amount: '',
        description: ''
    });

    // Subscription status (like financials page)
    const [subscriptionStatus, setSubscriptionStatus] = useState({
        loading: true,
        plan: null,
        isFreePlan: false,
        error: null
    });

    // Filter states (like financials page)
    const [filters, setFilters] = useState({
        type: 'all',
        search: ''
    });

    const [filteredTransactions, setFilteredTransactions] = useState([]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [transactionsPerPage] = useState(20);
    const [totalPages, setTotalPages] = useState(0);

    // Get theme colors from settings
    const bgColor = settings.block_title_bg_color || '#f8f9fa';
    const textColor = settings.block_title_color || '#8B4513';

    // Generate a unique CSS class name based on the current settings
    const themeClassName = `theme-header-${Date.now()}`;

    // Add CSS styles directly to the document head for dynamic theming
    useEffect(() => {
        const styleTag = document.createElement('style');
        styleTag.innerHTML = `
            /* Apply direct styles to these elements */
            .card-header.${themeClassName},
            .modal-header.${themeClassName} {
                background-color: ${bgColor} !important;
                color: ${textColor} !important;
                border-color: ${bgColor} !important;
            }
            
            /* Force all children to have the text color */
            .card-header.${themeClassName} *,
            .modal-header.${themeClassName} * {
                color: ${textColor} !important;
            }
            
            /* Override any other rules */
            .card .card-header.${themeClassName},
            .modal .modal-header.${themeClassName},
            .card-header.${themeClassName},
            .modal-header.${themeClassName},
            div.card-header.${themeClassName},
            div.modal-header.${themeClassName} {
                background-color: ${bgColor} !important;
                color: ${textColor} !important;
            }
            
            /* Direct style application */
            #root .card-header.${themeClassName},
            html body .card-header.${themeClassName} {
                background-color: ${bgColor} !important;
                color: ${textColor} !important;
            }
            
            /* For Description section specifically */
            .card-header.${themeClassName}[class*="theme-header"] {
                background-color: ${bgColor} !important;
                color: ${textColor} !important;
            }
        `;
        document.head.appendChild(styleTag);

        return () => {
            if (styleTag.parentNode) {
                document.head.removeChild(styleTag);
            }
        };
    }, [themeClassName, bgColor, textColor]);

    // Fetch corpus fund data function (following financials pattern)
    const fetchCorpusFundData = useCallback(async () => {
        // Skip fetching if user is on a free plan
        if (subscriptionStatus.isFreePlan && !subscriptionStatus.loading) {
            setLoading(false);
            return;
        }

        // Only fetch if subscription check is complete and not free plan
        if (!subscriptionStatus.loading && !subscriptionStatus.isFreePlan) {
            setLoading(true);
            setError(null);
            
            try {
            console.log('🔄 Fetching corpus fund data...');
            
            // Get transactions and summary data
            const [transactionsResponse, summaryResponse] = await Promise.all([
                corpusFundAPI.getAll(),
                corpusFundAPI.getSummary()
            ]);

            console.log('📦 Transactions response:', transactionsResponse);
            console.log('📦 Summary response:', summaryResponse);

            // Process transactions data - ensure it's always an array
            let transactionsData = transactionsResponse.data || [];
            
            // Handle different possible response structures
            if (transactionsResponse.data && transactionsResponse.data.data) {
                transactionsData = transactionsResponse.data.data;
            } else if (transactionsResponse.data && Array.isArray(transactionsResponse.data)) {
                transactionsData = transactionsResponse.data;
            } else if (!Array.isArray(transactionsData)) {
                console.warn('⚠️ Transactions data is not an array, converting to empty array');
                transactionsData = [];
            }
            
            console.log('📦 Processed transactions data:', transactionsData);
            console.log('📦 Transactions data type:', typeof transactionsData);
            console.log('📦 Is array:', Array.isArray(transactionsData));
            
            setTransactions(transactionsData);
            
            // Calculate pagination
            // Calculate pagination will be updated in the filter useEffect
            
            // Process summary data - ensure it's always an object
            let summaryData = summaryResponse.data || {};
            
            // Handle different possible response structures
            if (summaryResponse.data && summaryResponse.data.data) {
                summaryData = summaryResponse.data.data;
            } else if (summaryResponse.data && typeof summaryResponse.data === 'object') {
                summaryData = summaryResponse.data;
            }
            
            console.log('📦 Raw summary data:', summaryData);
            console.log('📦 Summary data type:', typeof summaryData);
            
            const processedSummary = {
                total_income: parseFloat(summaryData.total_income || 0),
                total_expenses: parseFloat(summaryData.total_expenses || 0),
                balance: parseFloat(summaryData.balance || 0),
                total_transactions: parseInt(summaryData.total_transactions || (Array.isArray(transactionsData) ? transactionsData.length : 0))
            };
            
            console.log('📦 Processed summary:', processedSummary);
            setSummary(processedSummary);
            
            setError(null);
        } catch (error) {
            console.error('❌ Error fetching corpus fund data:', error);
            
            // Handle different types of errors
            if (error.response?.status === 401) {
                setError('Authentication required. Please log in again.');
                toast.error('Authentication required. Please log in again.');
            } else if (error.response?.status === 403) {
                setError('Access denied. You do not have permission to view this data.');
                toast.error('Access denied. You do not have permission to view this data.');
            } else {
                setError('Failed to load corpus fund data. Please try again.');
                toast.error('Failed to load corpus fund data. Please try again.');
            }
            
            // Set default values on error
            setTransactions([]);
            setSummary({
                total_income: 0,
                total_expenses: 0,
                balance: 0,
                total_transactions: 0
            });
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
        }
    }, [transactionsPerPage, subscriptionStatus]);

    // Check subscription status (like financials page)
    useEffect(() => {
        const checkSubscription = async () => {
            try {
                // Include tenant/organization ID to target the correct subscription
                const userStr = sessionStorage.getItem('user');
                const userObj = userStr ? JSON.parse(userStr) : {};
                const tenantId = userObj.tenant_id || userObj.organization_id;
                const response = await axios.get('/api/saas/membership/details', {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    params: { tenant_id: tenantId }
                });

                if (response.data && response.data.success) {
                    const planName = response.data.data.plan?.name || 'Unknown';
                    setSubscriptionStatus({
                        loading: false,
                        plan: planName,
                        isFreePlan: planName === 'Free',
                        error: null
                    });
                } else {
                    // Default to Free plan if no subscription found
                    setSubscriptionStatus({
                        loading: false,
                        plan: 'Free',
                        isFreePlan: true,
                        error: null
                    });
                }
            } catch (error) {
                // Default to Free plan if there's an error
                setSubscriptionStatus({
                    loading: false,
                    plan: 'Free',
                    isFreePlan: true,
                    error: 'Failed to check subscription status'
                });
            }
        };

        checkSubscription();
    }, []);

    // Initial data fetch (following financials pattern)
    useEffect(() => {
        fetchCorpusFundData();
    }, [fetchCorpusFundData]);

    // Filter transactions (like financials page)
    useEffect(() => {
        let filtered = [...transactions];

        // Apply type filter
        if (filters.type !== 'all') {
            filtered = filtered.filter(t => t.type === filters.type);
        }

        // Apply search filter
        if (filters.search.trim() !== '') {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(t =>
                (t.description && t.description.toLowerCase().includes(searchTerm)) ||
                (t.amount && t.amount.toString().includes(searchTerm)) ||
                (t.created_by_name && t.created_by_name.toLowerCase().includes(searchTerm))
            );
        }

        setFilteredTransactions(filtered);
        setCurrentPage(1); // Reset to first page when filters change
        setTotalPages(Math.ceil(filtered.length / transactionsPerPage));
    }, [transactions, filters]);

    // Monitor state changes
    useEffect(() => {
        console.log('State updated - transactions:', transactions);
        console.log('State updated - summary:', summary);
    }, [transactions, summary]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }

        if (!formData.description.trim()) {
            toast.error('Please enter a description');
            return;
        }

        try {
            console.log('🔄 Saving corpus fund transaction...');
            
            if (editingTransaction) {
                await corpusFundAPI.update(editingTransaction.id, formData);
                toast.success('Transaction updated successfully');
            } else {
                await corpusFundAPI.create(formData);
                toast.success('Transaction added successfully');
            }
            
            setShowModal(false);
            setEditingTransaction(null);
            setFormData({ type: 'income', amount: '', description: '' });
            setCurrentPage(1); // Reset to first page after adding/editing
            
            // Re-fetch data to update summary (following financials pattern)
            await fetchCorpusFundData();
        } catch (error) {
            console.error('❌ Error saving transaction:', error);
            toast.error(error.response?.data?.message || 'Failed to save transaction');
        }
    };

    // Check if transaction is editable (within 30 minutes)
    const isTransactionEditable = (transaction) => {
        if (!transaction || !transaction.created_at) return false;
        
        const createdAt = new Date(transaction.created_at);
        const now = new Date();
        const timeDifference = now - createdAt;
        const thirtyMinutes = 30 * 60 * 1000; // 30 minutes in milliseconds
        
        return timeDifference <= thirtyMinutes;
    };

    const handleView = (transaction) => {
        setViewingTransaction(transaction);
        setShowViewModal(true);
    };

    const handleEdit = async (transaction) => {
        // Check if transaction is editable without API call
        if (!isTransactionEditable(transaction)) {
            toast.error('Transaction can only be edited within 30 minutes of creation');
            return;
        }

        setEditingTransaction(transaction);
        setFormData({
            type: transaction.type,
            amount: transaction.amount.toString(),
            description: transaction.description
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingTransaction(null);
        setFormData({ type: 'income', amount: '', description: '' });
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setViewingTransaction(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleResetFilters = () => {
        setFilters({
            type: 'all',
            search: ''
        });
    };

    // Pagination functions (using filtered transactions)
    const indexOfLastTransaction = currentPage * transactionsPerPage;
    const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
    const currentTransactions = Array.isArray(filteredTransactions) 
        ? filteredTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction)
        : [];

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    if (loading && subscriptionStatus.loading) {
        return (
            <div className="container-fluid mt-4 activity-container">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading corpus fund data...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Show upgrade message for Free plan users
    if (subscriptionStatus.isFreePlan) {
        return (
            <div className="container-fluid mt-4 activity-container">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
                    <h1 className="page-title">
                        <i className="fas fa-piggy-bank"></i> Corpus Fund Management
                    </h1>
                </div>

                <div className="card shadow-sm">
                    <div className="card-body text-center p-5">
                        <i className="fas fa-lock text-secondary mb-3" style={{ fontSize: '3rem' }}></i>
                        <h3>Corpus Fund Features Locked</h3>
                        <p className="text-muted mb-4">
                            Corpus fund management features are only available on the Pro plan.
                            Upgrade your subscription to access corpus fund tracking, reporting and management.
                        </p>

                        <div className="row justify-content-center mb-4">
                            <div className="col-md-6">
                                <div className="card bg-light">
                                    <div className="card-body">
                                        <h5 className="card-title">Pro Plan Features:</h5>
                                        <ul className="list-unstyled text-start">
                                            <li className="mb-2"><i className="fas fa-check text-success me-2"></i> Corpus fund transaction tracking</li>
                                            <li className="mb-2"><i className="fas fa-check text-success me-2"></i> Income and expense management</li>
                                            <li className="mb-2"><i className="fas fa-check text-success me-2"></i> Corpus fund reporting and analytics</li>
                                            <li className="mb-2"><i className="fas fa-check text-success me-2"></i> Transaction history and audit trail</li>
                                            <li className="mb-2"><i className="fas fa-check text-success me-2"></i> Corpus fund summary dashboards</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <a href="/admin/membership" className="btn btn-primary btn-lg">
                            <i className="fas fa-arrow-up me-2"></i> Upgrade to Pro Plan
                        </a>
                    </div>
                </div>
            </div>
        );
    }



    return (
        <div className="container-fluid mt-4 activity-container">
            {/* Header */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
                <h1 className="page-title">
                    <i className="fas fa-piggy-bank"></i> Corpus Fund Management
                </h1>
                <div className="d-flex flex-wrap gap-2">
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowModal(true)}
                    >
                        <i className="fas fa-plus me-2"></i>
                        Add Transaction
                    </button>
                </div>
            </div>
            
            {/* Corpus Fund Summary Stats */}
            <div className="financial-stats-container">
                <div className="financial-stat-card income">
                    <div className="stat-title">TOTAL INCOME</div>
                    <div className="stat-value" title={formatCurrency(summary.total_income || 0)}>
                        {formatCurrency(summary.total_income || 0)}
                    </div>
                    <div className="stat-description">
                        All corpus fund income
                    </div>
                </div>

                <div className="financial-stat-card expenses">
                    <div className="stat-title">TOTAL EXPENSES</div>
                    <div className="stat-value" title={formatCurrency(summary.total_expenses || 0)}>
                        {formatCurrency(summary.total_expenses || 0)}
                    </div>
                    <div className="stat-description">
                        All corpus fund expenses
                    </div>
                </div>

                <div className="financial-stat-card balance">
                    <div className="stat-title">CURRENT BALANCE</div>
                    <div className="stat-value" title={formatCurrency(summary.balance || 0)}>
                        {formatCurrency(summary.balance || 0)}
                    </div>
                    <div className="stat-description">
                        Net corpus fund balance
                    </div>
                </div>

                <div className="financial-stat-card pending">
                    <div className="stat-title">TOTAL TRANSACTIONS</div>
                    <div className="stat-value" title={summary.total_transactions || 0}>
                        {summary.total_transactions || 0}
                    </div>
                    <div className="stat-description">
                        Total transaction count
                    </div>
                </div>
            </div>

            {/* Transactions Section */}
            <div className="card shadow-sm">
                <div className={`card-header ${themeClassName}`}>
                    <h5 className="mb-0">
                        <i className="fas fa-list me-2"></i>
                        Corpus Fund Transactions
                    </h5>
                </div>

                {/* Filters Section */}
                <div className="card-body border-bottom">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label fw-bold">
                                <i className="fas fa-filter me-1"></i>
                                Transaction Type
                            </label>
                            <select
                                name="type"
                                value={filters.type}
                                onChange={handleFilterChange}
                                className="form-select"
                            >
                                <option value="all">All Types</option>
                                <option value="income">Income</option>
                                <option value="expense">Expense</option>
                            </select>
                        </div>
                        
                        <div className="col-md-6">
                            <label className="form-label fw-bold">
                                <i className="fas fa-search me-1"></i>
                                Search
                            </label>
                            <input
                                type="text"
                                name="search"
                                value={filters.search}
                                onChange={handleFilterChange}
                                className="form-control"
                                placeholder="Search by description, amount, or created by..."
                            />
                        </div>
                        
                        <div className="col-md-3 d-flex align-items-end">
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                className="btn btn-outline-secondary py-2"
                                title="Reset all filters"
                            >
                                <i className="fas fa-undo me-1"></i>
                                Reset Filters
                            </button>
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    {/* Transactions Table */}
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Amount</th>
                                    <th>Description</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!Array.isArray(filteredTransactions) || filteredTransactions.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="text-center text-muted py-4">
                                                <i className="fas fa-inbox fa-2x mb-3 d-block"></i>
                                                {filters.type !== 'all' || filters.search.trim() !== '' 
                                                    ? 'No transactions match your filters' 
                                                    : 'No transactions found'}
                                                {(filters.type !== 'all' || filters.search.trim() !== '') && (
                                                    <div className="mt-2">
                                                        <button
                                                            type="button"
                                                            onClick={handleResetFilters}
                                                            className="btn btn-sm btn-outline-primary"
                                                        >
                                                            <i className="fas fa-undo me-1"></i>
                                                            Clear Filters
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ) : (
                                    currentTransactions.map((transaction) => (
                                        <tr key={transaction.id}>
                                            <td>{formatDate(transaction.created_at)}</td>
                                            <td>
                                                <span className={`badge ${transaction.type === 'income' ? 'bg-success' : 'bg-danger'}`}>
                                                    {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-bold">
                                                    {formatCurrency(transaction.amount)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="text-truncate d-inline-block" style={{ maxWidth: '200px' }} title={transaction.description}>
                                                    {transaction.description}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="btn-group" role="group">
                                                    <button
                                                        onClick={() => handleView(transaction)}
                                                        className="btn btn-sm btn-outline-secondary"
                                                        title="View transaction"
                                                    >
                                                        <i className="fas fa-eye"></i>
                                                    </button>
                                                    {isTransactionEditable(transaction) && (
                                                        <button
                                                            onClick={() => handleEdit(transaction)}
                                                            className="btn btn-sm btn-outline-primary"
                                                            title="Edit transaction (within 30 minutes)"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {filteredTransactions.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="text-muted">
                                Showing {indexOfFirstTransaction + 1} to {Math.min(indexOfLastTransaction, filteredTransactions.length)} of {filteredTransactions.length} transactions
                            </div>
                            <nav aria-label="Corpus fund transactions pagination">
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={goToPreviousPage}
                                            disabled={currentPage === 1}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                    </li>
                                    
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                                        <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                                            <button
                                                className="page-link"
                                                onClick={() => paginate(number)}
                                            >
                                                {number}
                                            </button>
                                        </li>
                                    ))}
                                    
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={goToNextPage}
                                            disabled={currentPage === totalPages}
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className={`modal-header ${themeClassName}`}>
                                <h5 className="modal-title">
                                    <i className="fas fa-plus me-2"></i>
                                    {editingTransaction ? 'Edit Transaction' : 'Add Transaction'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={handleCloseModal}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label corpus_fund_type_label">
                                            <i className="fas fa-tag me-1"></i>
                                            Transaction Type
                                        </label>
                                        <select
                                            name="type"
                                            value={formData.type}
                                            onChange={handleInputChange}
                                            className="form-select corpus_fund_type_input"
                                            required
                                        >
                                            <option value="income">Income</option>
                                            <option value="expense">Expense</option>
                                        </select>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label corpus_fund_amount_label">
                                            <i className="fas fa-money-bill me-1"></i>
                                            Amount
                                        </label>
                                        <input
                                            type="text"
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleInputChange}
                                            className="form-control corpus_fund_amount_input"
                                            placeholder="0.00"
                                            onInput={(e) => {
                                                e.target.value = e.target.value.replace(/[^0-9]/g, '');
                                              }}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label corpus_fund_description_label">
                                            <i className="fas fa-align-left me-1"></i>
                                            Description
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            rows="3"
                                            className="form-control corpus_fund_description_input"
                                            placeholder="Enter transaction description..."
                                            required
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleCloseModal}
                                    >
                                        <i className="fas fa-times me-1"></i>
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                    >
                                        <i className="fas fa-save me-1"></i>
                                        {editingTransaction ? 'Update' : 'Submit'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* View Transaction Modal */}
            {showViewModal && viewingTransaction && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className={`modal-header ${themeClassName}`}>
                                <h5 className="modal-title">
                                    <i className="fas fa-eye me-2"></i>
                                    View Transaction
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={handleCloseViewModal}
                                    aria-label="Close"
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">
                                            <i className="fas fa-calendar me-1"></i>
                                            Date Created
                                        </label>
                                        <p className="form-control-plaintext">
                                            {formatDate(viewingTransaction.created_at)}
                                        </p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">
                                            <i className="fas fa-tag me-1"></i>
                                            Transaction Type
                                        </label>
                                        <p className="form-control-plaintext">
                                            <span className={`badge ${viewingTransaction.type === 'income' ? 'bg-success' : 'bg-danger'}`}>
                                                {viewingTransaction.type.charAt(0).toUpperCase() + viewingTransaction.type.slice(1)}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">
                                            <i className="fas fa-money-bill me-1"></i>
                                            Amount
                                        </label>
                                        <p className="form-control-plaintext">
                                            <span className="fw-bold">
                                                {formatCurrency(viewingTransaction.amount)}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">
                                            <i className="fas fa-align-left me-1"></i>
                                            Description
                                        </label>
                                        <p className="form-control-plaintext">
                                            {viewingTransaction.description}
                                        </p>
                                    </div>
                                </div>

                                {!isTransactionEditable(viewingTransaction) && (
                                    <div className="alert alert-info">
                                        <i className="fas fa-info-circle me-1"></i>
                                        This transaction can no longer be edited (30-minute limit exceeded)
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleCloseViewModal}
                                >
                                    <i className="fas fa-times me-1"></i>
                                    Close
                                </button>
                                {isTransactionEditable(viewingTransaction) && (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            handleCloseViewModal();
                                            handleEdit(viewingTransaction);
                                        }}
                                    >
                                        <i className="fas fa-edit me-1"></i>
                                        Edit Transaction
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
